import { useState } from 'react'
import {
  daysSinceLatestBuild,
  isBuildHistoryStale,
} from '../../domain/value-objects/buildHistoryFreshness'
import { useTranslation } from '../i18n'
import type { DashboardSummary } from '../types'

interface BuildHistoryWarningProps {
  data: DashboardSummary | null
  loading: boolean
  now?: Date
}

export function BuildHistoryWarning({ data, loading, now = new Date() }: BuildHistoryWarningProps) {
  const { t } = useTranslation()
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const latestBuild = data?.latestBuild
  if (loading || !latestBuild) return null

  const latestBuildTimestamp = new Date(latestBuild.timestamp)
  if (!isBuildHistoryStale(latestBuildTimestamp, now)) return null

  const days = Math.floor(daysSinceLatestBuild(latestBuildTimestamp, now))
  const summary = t('warning.staleBuildHistory', {
    days,
    date: latestBuildTimestamp.toLocaleDateString(),
  })
  const detail = t('warning.staleBuildHistoryDetail')

  return (
    <span
      className="build-history-warning stat-warning"
      data-testid="build-history-warning"
      role="status"
      tabIndex={0}
      aria-label={`${summary} ${detail}`}
      onMouseEnter={() => setTooltipVisible(true)}
      onMouseLeave={() => setTooltipVisible(false)}
      onFocus={() => setTooltipVisible(true)}
      onBlur={() => setTooltipVisible(false)}
    >
      <i className="pi pi-bell" aria-hidden="true" />
      {tooltipVisible && (
        <span className="build-history-warning-tooltip" data-testid="build-history-warning-tooltip">
          <strong>{summary}</strong>
          <span>{detail}</span>
        </span>
      )}
    </span>
  )
}
